import type { ScoringConfig, ScoringConfigUpdate } from '../../common/api/scorecardThresholds';

export type ThresholdDraft = { threshold: string; allocation: string };
export type StatusDraft = { extremely_low: string; below: string; above: string };
export type MetricDraft = {
  thresholds: ThresholdDraft[];
  status: StatusDraft;
  critical_threshold: string;
};
export type ThresholdsDraft = {
  review_volume: {
    customer_volume_defaults: Record<string, string>;
    customer_terms: Record<string, string>;
    source_map: Record<string, string>;
    unknown_source_default: string;
    unknown_business_type_default: string;
    thresholds: Record<string, Record<string, ThresholdDraft[]>>;
    statuses: Record<string, Record<string, StatusDraft>>;
    critical_thresholds: Record<string, Record<string, string>>;
  };
  average_rating: MetricDraft;
  response_rate: MetricDraft;
  response_time: MetricDraft;
  weights: Record<string, string>;
  grade_thresholds: Record<string, string>;
};

export type ValidationResult = {
  errors: Record<string, string>;
  update?: ScoringConfigUpdate;
};

export const GRADE_ORDER = [
  'A+',
  'A',
  'A-',
  'B+',
  'B',
  'B-',
  'C+',
  'C',
  'C-',
  'D+',
  'D',
  'D-',
] as const;

export function orderedGradeEntries<T>(grades: Record<string, T>): Array<[string, T]> {
  const keys = Object.keys(grades);
  if (keys.length === GRADE_ORDER.length && GRADE_ORDER.every((grade) => grade in grades)) {
    return GRADE_ORDER.map((grade) => [grade, grades[grade]]);
  }
  return Object.entries(grades);
}

const numberString = (value: number) => String(Number(value.toFixed(6)));
const percentageString = (value: number) => numberString(value * 100);

const statusDraft = (status: { extremely_low: number; below: number; above: number }) => ({
  extremely_low: percentageString(status.extremely_low),
  below: percentageString(status.below),
  above: percentageString(status.above),
});

const metricDraft = (
  metric: ScoringConfig['average_rating'],
  criticalAsPercent = false,
): MetricDraft => ({
  thresholds: metric.thresholds.map((item) => ({
    threshold: numberString(item.threshold),
    allocation: numberString(item.allocation),
  })),
  status: statusDraft(metric.status),
  critical_threshold:
    criticalAsPercent && metric.critical_threshold !== -1
      ? percentageString(metric.critical_threshold)
      : numberString(metric.critical_threshold),
});

export function createThresholdsDraft(config: ScoringConfig): ThresholdsDraft {
  const review = config.review_volume;
  return {
    review_volume: {
      customer_terms: { ...review.customer_terms },
      customer_volume_defaults: Object.fromEntries(
        Object.entries(review.customer_volume_defaults).map(([key, value]) => [
          key,
          numberString(value),
        ]),
      ),
      source_map: { ...review.source_map },
      unknown_source_default: review.unknown_source_default,
      unknown_business_type_default: review.unknown_business_type_default,
      thresholds: Object.fromEntries(
        Object.entries(review.thresholds).map(([source, businessTypes]) => [
          source,
          Object.fromEntries(
            Object.entries(businessTypes).map(([businessType, thresholds]) => [
              businessType,
              thresholds.map((item) => ({
                threshold: numberString(item.threshold),
                allocation: numberString(item.allocation),
              })),
            ]),
          ),
        ]),
      ),
      statuses: Object.fromEntries(
        Object.entries(review.statuses).map(([source, businessTypes]) => [
          source,
          Object.fromEntries(
            Object.entries(businessTypes).map(([businessType, status]) => [
              businessType,
              statusDraft(status),
            ]),
          ),
        ]),
      ),
      critical_thresholds: Object.fromEntries(
        Object.entries(review.critical_thresholds).map(([source, businessTypes]) => [
          source,
          Object.fromEntries(
            Object.entries(businessTypes).map(([businessType, value]) => [
              businessType,
              percentageString(value),
            ]),
          ),
        ]),
      ),
    },
    average_rating: metricDraft(config.average_rating),
    response_rate: metricDraft(config.response_rate, true),
    response_time: metricDraft(config.response_time, true),
    weights: Object.fromEntries(
      Object.entries(config.weights).map(([key, value]) => [key, percentageString(value)]),
    ),
    grade_thresholds: Object.fromEntries(
      orderedGradeEntries(config.grade_thresholds).map(([key, value]) => [
        key,
        percentageString(value),
      ]),
    ),
  };
}

function numeric(
  value: string,
  path: string,
  errors: Record<string, string>,
  options: { min?: number; max?: number; positive?: boolean } = {},
): number {
  const parsed = Number(value.replaceAll(',', ''));
  if (value.trim() === '' || !Number.isFinite(parsed)) {
    errors[path] = 'Enter a number.';
    return 0;
  }
  if (options.positive && parsed <= 0) errors[path] = 'Enter a value greater than 0.';
  if (options.min !== undefined && parsed < options.min)
    errors[path] = `Enter ${options.min} or more.`;
  if (options.max !== undefined && parsed > options.max)
    errors[path] = `Enter ${options.max} or less.`;
  return parsed;
}

function validateCurve(
  draft: ThresholdDraft[],
  path: string,
  errors: Record<string, string>,
  order: 'ascending' | 'descending',
  thresholdRange: { min: number; max?: number },
) {
  const result = draft.map((item, index) => ({
    threshold: numeric(item.threshold, `${path}.${index}.threshold`, errors, thresholdRange),
    allocation: numeric(item.allocation, `${path}.${index}.allocation`, errors, {
      min: 0,
      max: 100,
    }),
  }));
  for (let index = 1; index < result.length; index += 1) {
    const ordered =
      order === 'descending'
        ? result[index - 1].threshold > result[index].threshold
        : result[index - 1].threshold < result[index].threshold;
    if (!ordered && !errors[`${path}.${index}.threshold`])
      errors[`${path}.${index}.threshold`] = `Keep thresholds ${order}.`;
    if (
      result[index - 1].allocation < result[index].allocation &&
      !errors[`${path}.${index}.allocation`]
    )
      errors[`${path}.${index}.allocation`] = 'Keep allocations descending.';
  }
  return result;
}

function validateStatus(draft: StatusDraft, path: string, errors: Record<string, string>) {
  const values = {
    extremely_low: numeric(draft.extremely_low, `${path}.extremely_low`, errors, {
      min: 0,
      max: 100,
    }),
    below: numeric(draft.below, `${path}.below`, errors, { min: 0, max: 100 }),
    above: numeric(draft.above, `${path}.above`, errors, { min: 0, max: 100 }),
  };
  if (values.extremely_low >= values.below)
    errors[`${path}.below`] = 'Must be greater than Extremely low.';
  if (values.below >= values.above) errors[`${path}.above`] = 'Must be greater than Below average.';
  return {
    extremely_low: values.extremely_low / 100,
    below: values.below / 100,
    above: values.above / 100,
  };
}

function validateMetric(
  draft: MetricDraft,
  path: string,
  errors: Record<string, string>,
  options: {
    order: 'ascending' | 'descending';
    thresholdMax?: number;
    criticalMin: number;
    criticalMax?: number;
    criticalAsPercent?: boolean;
  },
) {
  return {
    thresholds: validateCurve(draft.thresholds, `${path}.thresholds`, errors, options.order, {
      min: 0,
      max: options.thresholdMax,
    }),
    status: validateStatus(draft.status, `${path}.status`, errors),
    critical_threshold: (() => {
      const value = numeric(draft.critical_threshold, `${path}.critical_threshold`, errors, {
        min: options.criticalMin,
        max: options.criticalMax,
      });
      return options.criticalAsPercent && value !== -1 ? value / 100 : value;
    })(),
  };
}

export function validateThresholdsDraft(draft: ThresholdsDraft): ValidationResult {
  const errors: Record<string, string> = {};
  const review = draft.review_volume;
  const businessTypes = Object.keys(review.customer_volume_defaults);
  const sources = Object.keys(review.thresholds);
  const customerTerms = Object.fromEntries(
    businessTypes.map((businessType) => {
      const term = (review.customer_terms[businessType] ?? '').trim();
      if (!term || term.length > 40) {
        errors[`review_volume.customer_terms.${businessType}`] =
          'Enter a customer term of 1 to 40 characters.';
      }
      return [businessType, term];
    }),
  );
  const customerVolumeDefaults = Object.fromEntries(
    businessTypes.map((businessType) => [
      businessType,
      numeric(
        review.customer_volume_defaults[businessType],
        `review_volume.customer_volume_defaults.${businessType}`,
        errors,
        { positive: true },
      ),
    ]),
  );
  const thresholds: ScoringConfigUpdate['review_volume']['thresholds'] = {};
  const statuses: ScoringConfigUpdate['review_volume']['statuses'] = {};
  const criticalThresholds: ScoringConfigUpdate['review_volume']['critical_thresholds'] = {};
  for (const source of sources) {
    thresholds[source] = {};
    statuses[source] = {};
    criticalThresholds[source] = {};
    for (const businessType of businessTypes) {
      const base = `review_volume.${source}.${businessType}`;
      thresholds[source][businessType] = validateCurve(
        review.thresholds[source][businessType],
        `${base}.thresholds`,
        errors,
        'descending',
        { min: 0 },
      );
      statuses[source][businessType] = validateStatus(
        review.statuses[source][businessType],
        `${base}.status`,
        errors,
      );
      criticalThresholds[source][businessType] =
        numeric(review.critical_thresholds[source][businessType], `${base}.critical`, errors, {
          min: 0,
          max: 100,
        }) / 100;
    }
  }
  for (const [source, fallback] of Object.entries(review.source_map)) {
    if (!sources.includes(fallback))
      errors[`review_volume.source_map.${source}`] = 'Choose a configured source.';
  }
  if (!sources.includes(review.unknown_source_default))
    errors['review_volume.unknown_source_default'] = 'Choose a configured source.';
  if (!businessTypes.includes(review.unknown_business_type_default))
    errors['review_volume.unknown_business_type_default'] = 'Choose a business type.';

  const averageRating = validateMetric(draft.average_rating, 'average_rating', errors, {
    order: 'descending',
    thresholdMax: 5,
    criticalMin: 0,
    criticalMax: 5,
  });
  const responseRate = validateMetric(draft.response_rate, 'response_rate', errors, {
    order: 'descending',
    thresholdMax: 100,
    criticalMin: -1,
    criticalMax: 100,
    criticalAsPercent: true,
  });
  const responseTime = validateMetric(draft.response_time, 'response_time', errors, {
    order: 'ascending',
    criticalMin: -1,
    criticalMax: 100,
    criticalAsPercent: true,
  });
  const weights = Object.fromEntries(
    Object.entries(draft.weights).map(([key, value]) => [
      key,
      numeric(value, `weights.${key}`, errors, { min: 0, max: 100 }) / 100,
    ]),
  ) as ScoringConfigUpdate['weights'];
  if (Math.abs(Object.values(weights).reduce((sum, value) => sum + value, 0) - 1) > 1e-9)
    errors.weights = 'Weights must total 100%.';

  const gradeThresholds = Object.fromEntries(
    Object.entries(draft.grade_thresholds).map(([grade, value]) => [
      grade,
      numeric(value, `grade_thresholds.${grade}`, errors, { min: 0, max: 100 }) / 100,
    ]),
  );
  const gradeEntries = orderedGradeEntries(gradeThresholds);
  for (let index = 1; index < gradeEntries.length; index += 1) {
    if (gradeEntries[index - 1][1] <= gradeEntries[index][1])
      errors[`grade_thresholds.${gradeEntries[index][0]}`] = 'Keep grade bands descending.';
  }

  if (Object.keys(errors).length > 0) return { errors };
  return {
    errors,
    update: {
      review_volume: {
        customer_terms: customerTerms,
        customer_volume_defaults: customerVolumeDefaults,
        source_map: { ...review.source_map },
        unknown_source_default: review.unknown_source_default,
        unknown_business_type_default: review.unknown_business_type_default,
        thresholds,
        statuses,
        critical_thresholds: criticalThresholds,
      },
      average_rating: averageRating,
      response_rate: responseRate,
      response_time: responseTime,
      weights,
      grade_thresholds: gradeThresholds,
    },
  };
}
